import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { api } from "./lib/api";
import { trackPageView, trackPing } from "./lib/analytics";
import { FullLoader, Spinner } from "./components/common";
import { AppShell } from "./components/AppShell";
import { Logo } from "./components/Logo";
import { Splash } from "./components/Splash";
import { MaintenanceScreen } from "./components/MaintenanceScreen";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Activity from "./pages/Activity";
import Send from "./pages/Send";
import Receive from "./pages/Receive";
import AddMoney from "./pages/AddMoney";
import Withdraw from "./pages/Withdraw";
import Cards from "./pages/Cards";
import Crypto from "./pages/Crypto";
import Assistant from "./pages/Assistant";
import Profile from "./pages/Profile";
import Security from "./pages/Security";
import PersonalInfo from "./pages/PersonalInfo";
import AddressPage from "./pages/AddressPage";
import Notifications from "./pages/Notifications";
import Admin from "./pages/Admin";
import AdminPinSetup from "./pages/AdminPinSetup";
import Requests from "./pages/Requests";
import Split from "./pages/Split";
import JackSend from "./pages/JackSend";
import KYC from "./pages/KYC";
import Badge from "./pages/Badge";
import Savings from "./pages/Savings";
import Converter from "./pages/Converter";
import Recurring from "./pages/Recurring";
import Insights from "./pages/Insights";
import Statements from "./pages/Statements";
import Legal from "./pages/Legal";
import Referral from "./pages/Referral";
import Banking from "./pages/Banking";
import PaymentReturn from "./pages/PaymentReturn";
import AccountGuard from "./pages/AccountGuard";
import Family from "./pages/Family";
import Bridge from "./pages/Bridge";
import Appeal from "./pages/Appeal";

function ProtectedRoute({ children, admin }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  if (user.account_status === "banned" && user.role !== "admin"
      && !location.pathname.startsWith("/app/appeal")) {
    return <Navigate to="/app/appeal" replace />;
  }
  return children;
}

function AuthCallback() {
  const nav = useNavigate();
  const { setUser, triggerSplash } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) { nav("/login"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        setUser(data.user);
        triggerSplash();
        window.history.replaceState(null, "", "/app");
        nav("/app", { replace: true });
      } catch (e) {
        setError("Google sign-in failed. Please try again.");
      }
    })();
  }, [nav, setUser]);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-mesh px-6 py-10"
         style={{ paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))" }}>
      <div className="flex flex-col items-center gap-4 text-center">
        <Logo size={48} />
        {error ? (
          <>
            <p className="text-destructive text-sm">{error}</p>
            <button onClick={() => nav("/login")} className="text-primary text-sm font-semibold">Back to sign in</button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Spinner className="w-7 h-7 text-primary" />
            <span className="text-sm">Signing you in…</span>
          </div>
        )}
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user, showSplash, endSplash, showLogout, endLogout } = useAuth();
  const [maint, setMaint] = useState(null);
  useEffect(() => {
    api.get("/maintenance").then((r) => setMaint(r.data)).catch(() => setMaint({ enabled: false }));
  }, [location.pathname]);

  const isAdmin = user?.role === "admin";
  const path = location.pathname;
  const bypass = isAdmin || path.startsWith("/admin") || path === "/login" || location.hash.includes("session_id=");
  if (maint?.enabled && !bypass) return <MaintenanceScreen message={maint.message} />;

  const content = location.hash?.includes("session_id=") ? (
    <AuthCallback />
  ) : (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/legal/:doc" element={<Legal />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin/pin-setup" element={<AdminPinSetup />} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentReturn cancelled /></ProtectedRoute>} />
      <Route path="/app/assistant" element={<ProtectedRoute><Assistant /></ProtectedRoute>} />
      <Route path="/app/appeal" element={<ProtectedRoute><Appeal /></ProtectedRoute>} />
      <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="activity" element={<Activity />} />
        <Route path="send" element={<Send />} />
        <Route path="request" element={<Send initialMode="request" />} />
        <Route path="receive" element={<Receive />} />
        <Route path="add-money" element={<AddMoney />} />
        <Route path="withdraw" element={<Withdraw />} />
        <Route path="cards" element={<Cards />} />
        <Route path="crypto" element={<Crypto />} />
        <Route path="requests" element={<Requests />} />
        <Route path="split" element={<Split />} />
        <Route path="jacksend" element={<JackSend />} />
        <Route path="kyc" element={<KYC />} />
        <Route path="badge" element={<Badge />} />
        <Route path="guard" element={<AccountGuard />} />
        <Route path="family" element={<Family />} />
        <Route path="bridge" element={<Bridge />} />
        <Route path="savings" element={<Savings />} />
        <Route path="converter" element={<Converter />} />
        <Route path="recurring" element={<Recurring />} />
        <Route path="insights" element={<Insights />} />
        <Route path="statements" element={<Statements />} />
        <Route path="referral" element={<Referral />} />
        <Route path="banking" element={<Banking />} />
        <Route path="profile" element={<Profile />} />
        <Route path="personal" element={<PersonalInfo />} />
        <Route path="address" element={<AddressPage />} />
        <Route path="security" element={<Security />} />
        <Route path="notifications" element={<Notifications />} />
      </Route>
      <Route path="/admin" element={<ProtectedRoute admin><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
  return (
    <>
      {content}
      <AnalyticsTracker />
      {showSplash && <Splash type="login" onEnd={endSplash} />}
      {showLogout && <Splash type="logout" onEnd={endLogout} />}
    </>
  );
}

function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
  useEffect(() => {
    const id = setInterval(() => trackPing(window.location.pathname), 20000);
    trackPing(pathname);
    return () => clearInterval(id);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}
